import { ChartBarData } from '../../../components/ChartBar';
import { SummedReport, PricebookReport, ReportChart } from '../../../models/ReportData';
import { findChartablePricebookReports } from './pricebookReportUtils';

const hasNonZeroData = (
  pricebookReport: PricebookReport,
  dataKeys: (keyof PricebookReport)[],
): boolean => {
  return dataKeys.some(key => {
    const value = pricebookReport[key];
    return typeof value === 'number' && value !== 0;
  });
};

const accumulateChartData = (
  previousData: ChartBarData[],
  pricebookReport: PricebookReport,
  dataMap: Map<keyof PricebookReport, string>,
): ChartBarData[] => {
  if (!pricebookReport) return previousData;

  const previousDataMap = new Map(previousData.map(entry => [entry.x, entry.y]));

  return Array.from(dataMap).map(([key, label]) => {
    const accumulatedValue = previousDataMap.get(label) ?? 0;
    const newValue = pricebookReport[key];
    const numericNewValue = typeof newValue === 'number' ? newValue : 0;

    return {
      x: label,
      y: numericNewValue + accumulatedValue,
    };
  });
};

const buildReportChartData = (
  summedReport: SummedReport,
  keyFunction: (pricebookReport: PricebookReport) => string,
  dataMap: Map<keyof PricebookReport, string>,
): ReportChart => {
  const chartData: ReportChart = {};
  const categories = [...dataMap.values()];
  const dataKeys = Array.from(dataMap.keys());
  const pricebookReports = findChartablePricebookReports(summedReport);

  pricebookReports.forEach((pricebookReport: PricebookReport) => {
    const key = keyFunction(pricebookReport);

    if (!key) return;

    if (hasNonZeroData(pricebookReport, dataKeys)) {
      if (!chartData[key]) {
        chartData[key] = Array.from(dataMap).map(([dataKey, label]) => {
          const value = pricebookReport[dataKey];
          return {
            x: label,
            y: typeof value === 'number' ? value : 0,
          };
        });
      } else {
        chartData[key] = accumulateChartData(chartData[key], pricebookReport, dataMap);
      }
    }
  });

  // Create empty 'Default' entry if no data
  if (Object.keys(chartData).length === 0) {
    chartData['Default'] = categories.map(category => ({ x: category, y: 0 }));
  }

  return chartData;
};

export { hasNonZeroData, accumulateChartData, buildReportChartData };
